import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Droplet, Home as HomeIcon, FileText, Settings, FlaskConical, Atom, ShieldCheck } from "lucide-react";
import { MaskTitle, Reveal } from "@/components/site/Reveal";
import { MiniFeatures, IconList } from "@/components/site/PageHero";
import LeadForm from "@/components/site/LeadForm";
import { INTERESTS } from "@/content";

const MINI = [
  { icon: Droplet, title: "Free Assessment", text: "No cost, no obligation" },
  { icon: HomeIcon, title: "In-Home Water Test", text: "Right here in San Diego" },
  { icon: FileText, title: "Personalized Recommendations", text: "Find the best solution for your home" },
];

export const TESTS = [
  { icon: Droplet, title: "Chlorine & Chloramine", text: "Used for disinfection, can affect taste, odor, and health" },
  { icon: Settings, title: "Hardness", text: "Can cause scale buildup and damage appliances" },
  { icon: FlaskConical, title: "Heavy Metals", text: "Such as lead, arsenic, and mercury" },
  { icon: Atom, title: "VOCs", text: "Volatile organic compounds from industrial and environmental sources" },
  { icon: ShieldCheck, title: "Sediment", text: "Dirt, sand, and rust from aging pipes and infrastructure" },
];

const STEPS = [
  { n: 1, title: "Request Your Assessment", text: "Fill out the form and tell us a little about your home." },
  { n: 2, title: "We Come to You", text: "Our team will schedule a convenient time for a free in-home water test at your home in San Diego." },
  { n: 3, title: "Get Your Results", text: "We'll review your results and provide personalized recommendations for the best water purification solution for your home." },
];

const MAP = { "fst-tank": INTERESTS[0], "fst-cartridge": INTERESTS[0] };

export default function SignUp() {
  const [params] = useSearchParams();
  const initial = MAP[params.get("product")] || "";

  return (
    <>
      <section data-testid="signup-hero" className="relative border-b border-[var(--line)]">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 py-6 lg:py-6">
          <div className="lg:col-span-7 relative">
            <div className="relative overflow-hidden min-h-[520px] lg:min-h-[500px]">
              <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.8 }} src="/img/house.jpg" alt="Modern San Diego home" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10" />
              <div className="relative p-2 sm:p-0 pt-10 lg:pt-12 sm:pr-10">
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow">Free Water Quality Assessment</motion.p>
                <MaskTitle testId="signup-title" lines={["Find out", "what's in", "your water."]} className="mt-5 text-[2.6rem] sm:text-6xl xl:text-[4.3rem]" />
                <Reveal delay={0.5}>
                  <p className="mt-6 text-[17px] text-[var(--ink)]/80 leading-relaxed max-w-md">Get a free, in-home water quality assessment and learn what's in your water, how it may be affecting your home and health, and which solutions are right for you.</p>
                </Reveal>
              </div>
            </div>
            <MiniFeatures items={MINI} testId="signup-mini-features" />
          </div>
          <Reveal delay={0.25} className="lg:col-span-5">
            <div data-testid="signup-form-card" className="bg-white border border-[var(--line)] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)] p-6 sm:p-8 lg:mt-4">
              <h2 className="headline text-[1.75rem] sm:text-3xl mb-6">Request your free<br />water assessment</h2>
              <LeadForm kind="assessment" key={initial} initialInterest={initial} />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="what-we-test" className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative aspect-[9/8] overflow-hidden"><img src="/img/test-kit.jpg" alt="Water testing kit with sample vial" className="absolute inset-0 h-full w-full object-cover" /></Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow">Get a Clearer Picture</p>
            <h2 data-testid="what-we-test-title" className="headline mt-4 text-4xl sm:text-5xl">What we test for</h2>
            <p className="mt-4 mb-8 text-[17px] text-[var(--body)] leading-relaxed">Our water quality assessment checks for common contaminants and conditions that can affect the taste, safety, and overall quality of your water.</p>
            <IconList items={TESTS} testId="test-list" />
          </Reveal>
        </div>
      </section>

      <section data-testid="simple-process" className="bg-[var(--soft)] py-16 lg:py-20">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
          <Reveal><p className="eyebrow">How It Works</p><h2 className="headline mt-4 text-4xl sm:text-5xl">A simple process.</h2></Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1} className="flex gap-5">
                <span className="grid place-items-center h-10 w-10 shrink-0 rounded-full bg-[var(--ink)] text-white font-head font-bold">{s.n}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-4"><h3 className="font-head font-semibold text-[16px]">{s.title}</h3>{i < 2 && <span className="hidden lg:block flex-1 h-px bg-[#D4D4D4]" />}</div>
                  <p className="mt-2 text-[16px] text-[var(--body)] leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
